'use client';

import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown
} from 'lucide-react';
import { Input } from '../ui/input';
import { intakeTableColumns } from '../../config/constant';
import { useNavigate } from 'react-router-dom';
import { truncateLob } from '../../utils/generalUtils';
import { TooltipWrapper, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export function ApplicationsTable({
  selectedApplicationId,
  sortConfig,
  applications,
  handleSort,
  handleRowClick,
  handleRowDoubleClick,
  currPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange
}) {
  const [currentPage, setCurrentPage] = useState(currPage);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const navigate = useNavigate();

  const navigateToApplicationDetails = id => {
    navigate(`/app/intake/${id}`);
  };

  return (
    <div>
      <div className='overflow-x-auto'>
        <div className='max-h-[500px] overflow-y-auto'>
          <Table>
            <TableHeader className='bg-gray-50 sticky top-0 z-10'>
              <TableRow>
                {intakeTableColumns.map(({ key, label }) => (
                  <TableHead className='font-semibold' key={key}>
                    <div
                      className='flex items-center cursor-pointer whitespace-nowrap break-keep'
                      onClick={() => handleSort(key)}
                    >
                      {label}
                      <ArrowUpDown className='ml-1 h-4 w-4' />
                      {/*To ask if icon is suupose to be updated or not */}
                      {/* {sortConfig.column === key &&
                        (sortConfig.direction === "asc" ? (
                          <ArrowUp className="ml-1 h-6 w-4" />
                        ) : (
                          <ArrowDown className="ml-1 h-6 w-4" />
                        ))} */}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {applications.map((application, index) => (
                <TableRow
                  key={application.application_id}
                  className={`hover:bg-gray-50 ${selectedApplicationId === application.application_id
                    ? 'bg-[#FFF8F6]'
                    : ''
                    }`}
                  onClick={() => {
                    handleRowClick(
                      index,
                      application.application_id,
                      application.customer_name
                    );
                  }}
                  onDoubleClick={() =>
                    handleRowDoubleClick(application.application_id)
                  }
                >
                  <TableCell
                    className='font-semibold text-gray-900 cursor-pointer hover:underline'
                    onClick={() => {
                      handleRowDoubleClick(application.application_id);
                    }}
                  >
                    {application.customer_name || '—'}
                  </TableCell>
                  <TableCell>
                    {application?.lob || '—'}
                    {/* <TooltipWrapper>
                    <TooltipTrigger asChild>
                      <span className="cursor-pointer">
                        {truncateLob(application.lob)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{application.lob}</TooltipContent>
                  </TooltipWrapper> */}
                  </TableCell>
                  <TableCell>{application.agent_name || '—'}</TableCell>
                  <TableCell>
                    {application.application_classification || '—'}
                  </TableCell>
                  <TableCell className='whitespace-nowrap'>
                    <Badge
                      variant='outline'
                      className={`
            ${application.application_status === 'under-review'
                          ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                          : ''
                        }
            ${application.application_status === 'reject'
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : ''
                        }
          `}
                    >
                      {application.application_status || '—'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className='bg-blue-100 text-blue-700 border-blue-200'>
                      {application.priority || '—'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {application.date
                      ? new Date(application.date).toLocaleDateString('en-GB')
                      : '—'}
                  </TableCell>
                  <TableCell className='font-mono text-sm'>
                    {application.application_id}
                  </TableCell>
                  <TableCell>{application.dnw_decision}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className='flex items-center justify-between px-4 py-3 border-t'>
        <div className=' text-gray-500'>
          Showing {startItem}-{endItem} of {totalItems} applications
        </div>
        <div className='flex items-center space-x-2'>
          <Button
            variant='outline'
            size='icon'
            onClick={() => {
              setCurrentPage(1);
              onPageChange(1);
            }}
            disabled={currentPage === 1}
          >
            <ChevronsLeft className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            size='icon'
            onClick={() => {
              const newPage = Math.max(1, currentPage - 1);
              setCurrentPage(newPage);
              onPageChange(newPage);
            }}
            disabled={currentPage === 1}
          >
            <ChevronLeft className='h-4 w-4' />
          </Button>

          <div className='flex items-center gap-1'>
            <Button
              variant={currentPage === 1 ? 'default' : 'outline'}
              size='sm'
              className={
                currentPage === 1
                  ? 'bg-[#FF5533] hover:bg-[#E04A2F] text-white'
                  : ''
              }
              onClick={() => {
                setCurrentPage(1);
                onPageChange(1);
              }}
            >
              1
            </Button>

            {currentPage > 3 && <span className='mx-1'>...</span>}

            {currentPage > 2 && (
              <Button
                variant='outline'
                size='sm'
                onClick={() => {
                  const newPage = currentPage - 1;
                  setCurrentPage(newPage);
                  onPageChange(newPage);
                }}
              >
                {currentPage - 1}
              </Button>
            )}

            {currentPage !== 1 && currentPage !== totalPages && (
              <Button
                variant='default'
                size='sm'
                className='bg-[#FF5533] hover:bg-[#E04A2F] text-white'
                onClick={() => {
                  setCurrentPage(currentPage);
                  onPageChange(currentPage);
                }}
              >
                {currentPage}
              </Button>
            )}

            {currentPage < totalPages - 1 && (
              <Button
                variant='outline'
                size='sm'
                onClick={() => {
                  const newPage = currentPage + 1;
                  setCurrentPage(newPage);
                  onPageChange(newPage);
                }}
              >
                {currentPage + 1}
              </Button>
            )}

            {currentPage < totalPages - 2 && <span className='mx-1'>...</span>}

            {totalPages > 1 && (
              <Button
                variant={currentPage === totalPages ? 'default' : 'outline'}
                size='sm'
                className={
                  currentPage === totalPages
                    ? 'bg-[#FF5533] hover:bg-[#E04A2F] text-white'
                    : ''
                }
                onClick={() => {
                  setCurrentPage(totalPages);
                  onPageChange(totalPages);
                }}
              >
                {totalPages}
              </Button>
            )}
          </div>

          <Button
            variant='outline'
            size='icon'
            onClick={() => {
              const newPage = Math.min(totalPages, currentPage + 1);
              setCurrentPage(newPage);
              onPageChange(newPage);
            }}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            size='icon'
            onClick={() => {
              setCurrentPage(totalPages);
              onPageChange(totalPages);
            }}
            disabled={currentPage === totalPages}
          >
            <ChevronsRight className='h-4 w-4' />
          </Button>

          <div className='flex items-center ml-2'>
            <span className=' text-gray-500 mr-2 flex-shrink-0'>
              Go to
            </span>
            <Input
              type='number'
              min={1}
              max={totalPages}
              value={currentPage}
              onChange={e => {
                const value = Number.parseInt(e.target.value);
                if (value >= 1 && value <= totalPages) {
                  setCurrentPage(value);
                  onPageChange(value);
                }
              }}
              className='w-16 h-8'
            />
            <span className=' text-gray-500 ml-2 flex-shrink-0'>
              of {totalPages} pages
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
